export type Unsubscribe = () => void;

/** Minimal typed event emitter. `Events` is a `{ eventName: handlerFn }`
 * map — the same shape `CardFieldEvents`/`SessionEvents` use. Constrained
 * to `object` rather than an explicit string-indexed type: a plain
 * interface/type-literal listing concrete event names does not structurally
 * satisfy a `Record<string, ...>` constraint in TypeScript without an
 * explicit index signature, which callers of this emitter should never have
 * to add just to describe their event map. */
/** Extracts the argument tuple of an event's handler type, falling back to
 * `never[]` for a non-function member (which should not occur in practice —
 * every `Events` value is a handler function). */
type ArgsOf<F> = F extends (...args: infer A) => void ? A : never[];

export class Emitter<Events extends object> {
  private readonly listeners: { [K in keyof Events]?: Set<(...args: ArgsOf<Events[K]>) => void> } = {};

  on<K extends keyof Events>(event: K, callback: Events[K] & ((...args: ArgsOf<Events[K]>) => void)): Unsubscribe {
    let set = this.listeners[event];
    if (!set) {
      set = new Set();
      this.listeners[event] = set;
    }
    set.add(callback);
    return () => {
      set?.delete(callback);
    };
  }

  emit<K extends keyof Events>(event: K, ...args: ArgsOf<Events[K]>): void {
    const set = this.listeners[event];
    if (!set) return;
    for (const callback of Array.from(set)) {
      callback(...args);
    }
  }
}
